import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

import { getTokensByRefreshTokenApi } from 'api/auth';
import { CHIRP_EXPERIENCE_API_URL } from 'config';
import { StorageE } from 'declarations/enums/storage';
import type { ApiErrorDataI, ApiErrorI } from 'declarations/interfaces/errors';
import useAuthStore from 'hooks/store/useAuthStore';

const ApiError = (error: AxiosError<ApiErrorDataI>): ApiErrorI => ({
  name: error.name,
  code: error.code || null,
  data: error.response?.data || null,
});

const axiosInstance = axios.create({
  baseURL: `${CHIRP_EXPERIENCE_API_URL}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// An interceptor for Authorization header
axiosInstance.interceptors.request.use(
  config => {
    const token = localStorage.getItem(StorageE.IdToken);

    if (token && config.baseURL === CHIRP_EXPERIENCE_API_URL && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  error => {
    Promise.reject(error);
  }
);

// Refresh authorization function
const refreshAuthLogic = async (failedRequest: AxiosError): Promise<void> => {
  const refreshToken = localStorage.getItem(StorageE.RefreshToken);

  if (refreshToken) {
    return getTokensByRefreshTokenApi(refreshToken)
      .then(response => {
        localStorage.setItem(StorageE.IdToken, response.id_token);

        if (failedRequest.response?.config?.headers) {
          failedRequest.response.config.headers.Authorization = `Bearer ${response.id_token}`;
        }

        return Promise.resolve();
      })
      .catch(() => {
        localStorage.removeItem(StorageE.IdToken);
        localStorage.removeItem(StorageE.RefreshToken);
        useAuthStore.setState({ isUserLoggedIn: false });

        return Promise.reject();
      });
  }

  return Promise.reject();
};

createAuthRefreshInterceptor(axiosInstance, refreshAuthLogic);

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches WarrChirp; callers provide concrete DTO types
export async function get<ResponseI, RequestParamsI = any>(
  path: string,
  params?: RequestParamsI,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.get<ResponseI>(path, {
      params: params || {},
      ...config,
    });

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches WarrChirp; callers provide concrete DTO types
export async function post<ResponseI, RequestBodyI = any>(
  path: string,
  data?: RequestBodyI,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.post<ResponseI>(path, data, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- matches WarrChirp; callers provide concrete DTO types
export async function put<ResponseI, RequestBodyI = any>(
  path: string,
  data?: RequestBodyI,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.put<ResponseI>(path, data, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}

export async function remove<ResponseI>(
  path: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<ResponseI>> {
  try {
    const response = await axiosInstance.delete<ResponseI>(path, config);

    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw ApiError(error as AxiosError<ApiErrorDataI>);
    }

    throw new Error('Unexpected error');
  }
}
